<template>
  <section>
    <THeading
      :level="3"
      tag="h3"
      class="mb-4"
    >
      {{ t('title') }}
    </THeading>

    <div>
      {{ t('located', { city: rvLocation?.City, state: rvLocation?.State }) }}
    </div>

    <LazyRvRentalDeliveryRadiusImage
      v-if="rvLocation?.Latitude && rvLocation?.Longitude"
      :lat="rvLocation.Latitude"
      :lng="rvLocation.Longitude"
      :radius-in-km="deliveryDistanceInKms"
      class="my-4"
    />

    <TGrid
      class="mt-2"
      has-vertical-gap
    >
      <TGridItem
        v-if="!rvIsDeliveryOnly"
        :lg="6"
      >
        <strong>{{ t('pickup.title') }}</strong>
        <span class="text-gray-500 ml-2">{{ t('pickup.free') }}</span>
        <p>
          {{ t('pickup.text', { city: rvLocation?.City, state: rvLocation?.State }) }}
        </p>
      </TGridItem>

      <TGridItem :lg="rvIsDeliveryOnly ? 12 : 6">
        <strong>{{ t('delivery.title') }}</strong>
        <span class="text-gray-500 ml-2">{{ deliveryPriceDisplay }}</span>
        <p>
          {{ t('delivery.text', { amount: rvDeliveryMaxDistance, unit: rvMeasurementUnit }) }}
        </p>
        <p
          v-if="rvDeliveryInstructions"
          class="text-gray-500"
        >
          {{ t('delivery.note') }}{{ rvDeliveryInstructions }}
        </p>
      </TGridItem>
    </TGrid>

    <FormRvDeliveryAddress
      v-if="rvIsPublished"
      class="mt-4"
      :country-code="(rvCountry as COUNTRY_SHORTS)"
      :measurement-unit="rvMeasurementUnit"
      :delivery-max-distance="rvDeliveryMaxDistance"
      :delivery-minimum-cost-cents="rvDeliveryMinimumCostCents"
      :countries="allowedCountries"
      allow-request-to-book
      has-not-elligible-button
      @delivery-address-updated="deliveryAddressUpdated"
    />
  </section>
</template>

<script setup lang="ts">
import type { COUNTRY_SHORTS } from '~/constants'
import type { BookingEstimateDestination } from '~/types'

const {
  rvAllowCrossBorderTravel,
  rvCountry,
  rvDeliveryCentsPerDistanceUnit,
  rvLocation,
  rvMeasurementUnit,
  rvDeliveryMaxDistance,
  rvDeliveryMinimumCostCents,
  rvIsDeliveryOnly,
  rvIsPublished,
  rvDeliveryInstructions,
  rvIsFromUs,
} = useRvDetails()

const { updateDestination, updateRvUsage } = useBookingEstimate()

const { t } = useI18n()
const { priceDisplay } = usePrice()

const deliveryPriceDisplay = computed(() => {
  const deliveryPricePerUnitDollars = Number(rvDeliveryCentsPerDistanceUnit.value) / 100
  const price = priceDisplay({
    value: deliveryPricePerUnitDollars,
    countryCode: rvCountry.value,
    round: false,
    showCurrencyCode: true,
  })
  return t('delivery.pricePerUnit', { amount: price, unit: t(`delivery.${rvMeasurementUnit.value}`) })
})

const deliveryDistanceInKms = computed(() => rvIsFromUs.value ? miToKm(rvDeliveryMaxDistance.value ?? 0) : rvDeliveryMaxDistance.value ?? 0)

const allowedCountries = computed(() => {
  if (rvAllowCrossBorderTravel.value || !rvCountry.value) {
    return []
  }
  return [rvCountry.value]
})

const route = useRoute()
async function deliveryAddressUpdated(destinationData?: BookingEstimateDestination) {
  updateDestination(destinationData)

  if (!destinationData) {
    updateRvUsage('Pickup')
    await navigateTo({ query: { ...route.query, PlaceId: undefined, RvUsage: undefined } })
  }
  else {
    await navigateTo({ query: { ...route.query, PlaceId: destinationData?.placeId || undefined } })
  }
}
</script>

<i18n lang="json">
{
  "en": {
    "title": "Delivery options",
    "located": "RV located in {city}, {state}",
    "delivery": {
      "title": "Delivery",
      "text": "No driving necessary! Whether it’s to your campsite or your door, the host will deliver up to {amount} {unit} from their location.",
      "note": "A note from the host: ",
      "pricePerUnit": "{amount} per {unit}",
      "km": "km",
      "miles": "mile"
    },
    "pickup": {
      "title": "Pick up",
      "free": "Free",
      "text": "Pick up and drop off the RV yourself for free in {city}, {state}. Exact location shared after booking."
    }
  },
  "fr": {
    "title": "Options de livraison",
    "located": "VR situé à {city}, {state}",
    "delivery": {
      "title": "Livraison",
      "text": "Pas besoin de conduire! Que ce soit jusqu’à votre site de camping ou jusqu’à votre porte, l’hôte livrera jusqu’à {amount} {unit} de leur emplacement.",
      "note": "Une note de l’hôte: ",
      "pricePerUnit": "{amount} par {unit}",
      "km": "km",
      "miles": "mile"
    },
    "pickup": {
      "title": "Collecter",
      "free": "Gratuit",
      "text": "Collectez et déposez le VR vous-même gratuitement à {city}, {state}. Localisation exacte partagée après la réservation."
    }
  }
}
</i18n>
